
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const qualityTrackingKeys = {
  all: ['projectQualityTracking'],
  byProject: (projectId) => [...qualityTrackingKeys.all, 'project', projectId],
  byPhase: (projectId, phase) => [...qualityTrackingKeys.all, 'phase', projectId, phase],
  byTemplate: (projectId, templateId) => [...qualityTrackingKeys.all, 'template', projectId, templateId],
  specific: (projectId, taskId) => [...qualityTrackingKeys.all, 'specific', projectId, taskId],
  summary: (projectId) => [...qualityTrackingKeys.all, 'summary', projectId],
};

/**
 * Hook para obtener controles de calidad de un proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useProjectQualityTracking = (projectId, options = {}) => {
  return useQuery({
    queryKey: qualityTrackingKeys.byProject(projectId),
    queryFn: async () => {
      const response = await apiClient.get(`project_quality_tracking/by-project/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener controles de calidad por fase
 * @param {string} projectId - UUID del proyecto
 * @param {string} phase - Fase del control
 * @param {Object} options - Opciones adicionales para la query
 */
export const useQualityByPhase = (projectId, phase, options = {}) => {
  return useQuery({
    queryKey: qualityTrackingKeys.byPhase(projectId, phase),
    queryFn: async () => {
      const response = await apiClient.get(`project_quality_tracking/by-project/${projectId}/phase/${phase}`);
      return response.data;
    },
    enabled: !!(projectId && phase),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener resumen de calidad por fases
 * @param {string} projectId - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useQualitySummary = (projectId, options = {}) => {
  return useQuery({
    queryKey: qualityTrackingKeys.summary(projectId),
    queryFn: async () => {
      const response = await apiClient.get(`project_quality_tracking/by-project/${projectId}/summary`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener controles de calidad por template
 * @param {string} projectId - UUID del proyecto
 * @param {string} templateId - UUID del template
 * @param {Object} options - Opciones adicionales para la query
 */
export const useQualityByTemplate = (projectId, templateId, options = {}) => {
  return useQuery({
    queryKey: qualityTrackingKeys.byTemplate(projectId, templateId),
    queryFn: async () => {
      const response = await apiClient.get(`project_quality_tracking/by-template/${projectId}/${templateId}`);
      return response.data;
    },
    enabled: !!(projectId && templateId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener control de calidad específico
 * @param {string} projectId - UUID del proyecto
 * @param {string} taskId - UUID de la tarea
 * @param {Object} options - Opciones adicionales para la query
 */
export const useQualityTracking = (projectId, taskId, options = {}) => {
  return useQuery({
    queryKey: qualityTrackingKeys.specific(projectId, taskId),
    queryFn: async () => {
      const response = await apiClient.get(`project_quality_tracking/${projectId}/${taskId}`);
      return response.data;
    },
    enabled: !!(projectId && taskId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para actualizar control de calidad
 */
export const useUpdateQualityTracking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, taskId, data }) => {
      const response = await apiClient.put(`project_quality_tracking/${projectId}/${taskId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        qualityTrackingKeys.specific(variables.projectId, variables.taskId),
        data
      );
      queryClient.invalidateQueries({ queryKey: qualityTrackingKeys.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: qualityTrackingKeys.summary(variables.projectId) });
    },
    onError: (error) => {
      console.error('Error updating quality tracking:', error);
    },
  });
};

/**
 * Hook para actualización masiva de calidad
 */
export const useBulkUpdateQuality = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, qualityUpdates }) => {
      const response = await apiClient.put(`project_quality_tracking/bulk-update/${projectId}`, {
        quality_updates: qualityUpdates
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityTrackingKeys.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: qualityTrackingKeys.summary(variables.projectId) });
    },
    onError: (error) => {
      console.error('Error bulk updating quality:', error);
    },
  });
};